// Persistent screen router; logical transitions precede visual fades.

/** name -> { enter?(params, prevName), leave?(nextName) } */
const screens = {};
let current = "loading";
let visualEpoch = 0;
let dissolves = [];
document.documentElement.dataset.activeScreen = current;

// Only foreground elements dissolve. Never clone, replace or animate media,
// the canvas, ambient forms, wordmark, or their ambient animation timelines.
function foreground(section) {
  return [...section.querySelectorAll('h2,h3,button,input,.status,.error,.facts,.transcript,.timer,.banner,.remote-label')]
    .filter(el => !el.closest?.('.facts,.transcript') || el.matches?.('.facts,.transcript'));
}

function cancelDissolves() {
  for (const animation of dissolves) animation.cancel();
  dissolves = [];
}

/** Adds screen hooks, e.g. register({ "talker-matching": { enter() {…} } }). */
export function register(hooks) {
  Object.assign(screens, hooks);
}

/** Shows a screen. */
export function go(name, params = {}) {
  const next = document.querySelector(`section[data-screen="${name}"]`);
  if (!next) throw new Error(`unknown screen: ${name}`);
  const epoch = ++visualEpoch;
  cancelDissolves();
  const prev = current;
  const previous = document.querySelector(`section[data-screen="${prev}"]`);
  // Logical navigation and cleanup must complete in this same event turn.
  // Async screen modules retain their own epochs; visual completion never
  // invokes lifecycle hooks or user-gesture SDK work.
  screens[prev]?.leave?.(name);
  current = name;
  document.documentElement.dataset.activeScreen = name;
  for (const section of document.querySelectorAll('section[data-screen]')) {
    section.hidden = section !== next;
    section.inert = section !== next;
    section.removeAttribute('aria-busy');
    section.removeAttribute('aria-hidden');
    section.removeAttribute('data-leaving');
  }
  next.hidden = false;
  window.scrollTo(0, 0);
  screens[name]?.enter?.(params, prev);
  // enter() may synchronously redirect (an invalid/abandoned call).
  if (epoch !== visualEpoch) return;
  const reduced = matchMedia('(prefers-reduced-motion:reduce)').matches;
  if (reduced || ['gate', 'access'].includes(name) || ['gate', 'access'].includes(prev) || prev === name) return;
  document.activeElement?.blur();
  next.inert = true;
  next.setAttribute('aria-busy', 'true');
  // Keep exactly one logically visible section even during the dissolve.
  // CSS may render the inert, aria-hidden outgoing foreground briefly.
  previous.setAttribute('data-leaving', '');
  previous.inert = true;
  previous.setAttribute('aria-hidden', 'true');
  const animate = (section, entering) => foreground(section).flatMap((el, i) => {
    if (!el.animate) return [];
    const frames = entering
      ? [{ opacity: 0, filter: 'blur(5px)' }, { opacity: 1, filter: 'blur(0px)' }]
      : [{ opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(5px)' }];
    return [el.animate(frames, { duration: entering ? 300 : 180,
      delay: (entering ? 180 : 0) + Math.min(i, 4) * 16,
      easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' })];
  });
  const owned = [...animate(previous, false), ...animate(next, true)];
  dissolves = owned;
  Promise.all(owned.map(a => a.finished.catch(() => {}))).then(() => {
    if (epoch !== visualEpoch) return;
    previous.hidden = true;
    previous.removeAttribute('data-leaving');
    previous.removeAttribute('aria-hidden');
    next.inert = false;
    next.removeAttribute('aria-busy');
    cancelDissolves();
    const heading = next.querySelector('h2');
    heading?.setAttribute('tabindex', '-1');
    heading?.focus({ preventScroll: true });
  });
}

/** Any API call that comes back 401 sends the user to the gate. */
export function handleError(e) {
  if (e?.status === 401) go(e.data?.error === 'access_required' ? 'access' : 'gate');
  else if (e?.data?.error === 'profile_required') go('gate', { completeProfile: true });
  else console.error(e);
}

/** Value of the pressed button in a choice group, e.g. choice("talker-pref") -> "video". */
export function choice(name) {
  return document.querySelector(`[data-choice="${name}"] [aria-pressed="true"]`)?.dataset.value;
}

/** Shortcut for getElementById. */
export function $(id) {
  return document.getElementById(id);
}

// ---- generic wiring --------------------------------------------------------

// <button data-go="screen-name"> navigates.
document.addEventListener("click", (e) => {
  const target = e.target.closest("[data-go]");
  if (target && !target.disabled && !target.closest('[inert]')) go(target.dataset.go);
});

// Choice groups: exactly one button pressed (black fill).
document.addEventListener("click", (e) => {
  const btn = e.target.closest(".choices button");
  if (!btn) return;
  for (const b of btn.parentElement.querySelectorAll("button")) b.setAttribute("aria-pressed", String(b === btn));
});
